/**
 * Beat 14's staging, as pure simulation (`docs/beat-14-plan.md`).
 *
 * Everything that moves in the last beat is decided here on the fixed step —
 * the truck, the backstop walk to the bell, and the carry through the mouth
 * shot and the turn — so the no-input run is one deterministic function of the
 * story's cues and `dt`, and `beat-14-ending.spec.ts` can drive it in the sim
 * lane without a frame. The room draws it and voices it; the part frames it.
 *
 * **The carry is a shot, not a system** (D-095, and `docs/act-3-plan.md`: *"do
 * not build `carry` for this"*). From the mouth on, this class owns her body
 * through `RoomModule.carry`, with a `speed` so the host walks her legs.
 */
import type { CarriedPose } from '@/physics/character';
import { ENDING_SECONDS } from '@/story/act3/ending';
import { HILL_BELL_AT, hillGroundY, hillTruckGone, hillTruckX } from '@/world/hill';

export type EndingStage = 'the-hill' | 'the-grass' | 'the-find' | 'the-mouth' | 'the-turn' | 'black';

/** Where she stands to take it up: behind the bell on the uphill side, facing down at it. */
const TAKE_FROM = 0.34;
/** She turns to face the farm, which is +Z: facing 0. */
const FARM_FACING = 0;

/**
 * The backstop walk. If she wandered off further than this, the find opens on
 * a cut to the bell and she walks into it from `FIND_ENTRY` metres: an
 * authored walk of forty metres would be a second beat.
 */
const FIND_FAR = 8;
const FIND_ENTRY = 3;
/** The camera finds it first; she moves after. */
const FIND_HOLD = 0.8;

/** The mouth shot: the dip, the take, and the lift that rings it. */
export const MOUTH_MARKS = { dipped: 1.2, taken: 1.5, lifted: 2.3 } as const;

/**
 * The turn: round to the farm, then four steps, each one a stroke. The cut to
 * black is the NEXT stroke, which is the `black` cue's, not one of these.
 */
export const TURN_MARKS = { turned: 1.6, steps: [2.1, 3.1, 4.1, 5.1] } as const;
/** Her pace in the four steps. Tired, not stopped (the plan: wear). */
const STEP_SPEED = 0.34;
/** The speed the host animates while she turns on the spot: a shuffle. */
const TURN_SHUFFLE = 0.12;

export type EndingEvent =
  | { readonly kind: 'lift'; readonly x: number; readonly y: number; readonly z: number }
  | { readonly kind: 'step'; readonly index: number; readonly x: number; readonly y: number; readonly z: number }
  | { readonly kind: 'last'; readonly x: number; readonly y: number; readonly z: number };

export interface EndingSnapshot {
  readonly stage: EndingStage | null;
  readonly stageTime: number;
  readonly truckX: number;
  readonly truckGone: boolean;
  /** The bell is in her mouth from `MOUTH_MARKS.taken` on, for the rest of the game. */
  readonly bellHeld: boolean;
  /** 0 → 1 over the mouth shot's dip, and back to the carry's hold. Presentation reads it. */
  readonly headDip: number;
  readonly carry: CarriedPose | null;
  readonly steps: number;
}

function smooth(t: number): number {
  const k = Math.min(1, Math.max(0, t));
  return k * k * (3 - 2 * k);
}

function shortestTurn(from: number, to: number): number {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}

export class EndingStaging {
  #stage: EndingStage | null = null;
  #stageTime = 0;
  #truckTime = 0;
  #truckStarted = false;
  #bellHeld = false;
  #steps = 0;
  #lifted = false;
  #carry: CarriedPose | null = null;
  /** Where she stood when the stage that took her body began. */
  #from = { x: 0, z: 0, facing: 0 };
  /** Where the mouth shot holds her: fixed once, from wherever she came to the bell. */
  #take: { x: number; z: number; facing: number } = { x: HILL_BELL_AT.x, z: HILL_BELL_AT.z + TAKE_FROM, facing: Math.PI };
  readonly #events: EndingEvent[] = [];

  /** Enter a stage from its cue. `her` is copied, never held. */
  enter(stage: EndingStage, her: { readonly x: number; readonly z: number; readonly facing: number }): void {
    this.#stage = stage;
    this.#stageTime = 0;
    if (stage === 'the-hill') this.#truckStarted = true;
    if (stage === 'the-find') {
      const dx = her.x - HILL_BELL_AT.x;
      const dz = her.z - HILL_BELL_AT.z;
      const distance = Math.hypot(dx, dz);
      // Far off: she enters the find's frame from FIND_ENTRY metres, on her own
      // bearing to the bell, so the cut does not also turn her round.
      const k = distance > FIND_FAR ? FIND_ENTRY / distance : 1;
      this.#from = { x: HILL_BELL_AT.x + dx * k, z: HILL_BELL_AT.z + dz * k, facing: her.facing };
      this.#take = this.#takeFrom(this.#from.x, this.#from.z);
      this.#carry = this.#pose(this.#from.x, this.#from.z, this.#from.facing, 0);
    }
    if (stage === 'the-mouth') {
      // Found on her own: she is wherever the nudge reached it from, and the
      // take is from there, on her side of the bell.
      if (this.#carry === null) this.#take = this.#takeFrom(her.x, her.z);
      this.#carry = this.#pose(this.#take.x, this.#take.z, this.#take.facing, 0);
    }
    if (stage === 'the-turn') {
      this.#from = { ...this.#take };
    }
    if (stage === 'black') {
      const carry = this.#carry;
      if (carry !== null) this.#events.push({ kind: 'last', x: carry.x, y: carry.y + 0.1, z: carry.z });
      // Still: the stroke rings out over black and nothing follows it.
      if (carry !== null) this.#carry = { ...carry, speed: 0 };
    }
  }

  step(dt: number): void {
    this.#stageTime += dt;
    if (this.#truckStarted) this.#truckTime += dt;
    const t = this.#stageTime;
    if (this.#stage === 'the-find') {
      const walk = smooth((t - FIND_HOLD) / (ENDING_SECONDS.find - FIND_HOLD - 0.4));
      const x = this.#from.x + (this.#take.x - this.#from.x) * walk;
      const z = this.#from.z + (this.#take.z - this.#from.z) * walk;
      const bearing = Math.atan2(this.#take.x - this.#from.x, this.#take.z - this.#from.z);
      const moving = walk > 0 && walk < 1;
      const facing = walk >= 1 ? this.#take.facing : moving ? bearing : this.#from.facing;
      this.#carry = this.#pose(x, z, facing, moving ? 0.45 : 0);
    }
    if (this.#stage === 'the-mouth') {
      if (!this.#bellHeld && t >= MOUTH_MARKS.taken) this.#bellHeld = true;
      if (!this.#lifted && t >= MOUTH_MARKS.lifted) {
        this.#lifted = true;
        const carry = this.#carry;
        if (carry !== null) this.#events.push({ kind: 'lift', x: HILL_BELL_AT.x, y: carry.y + 0.12, z: HILL_BELL_AT.z });
      }
    }
    if (this.#stage === 'the-turn') {
      const turnK = smooth(t / TURN_MARKS.turned);
      const facing = this.#take.facing + shortestTurn(this.#take.facing, FARM_FACING) * turnK;
      const walking = Math.max(0, t - TURN_MARKS.turned);
      const x = this.#take.x;
      const z = this.#take.z + STEP_SPEED * walking;
      const speed = t < TURN_MARKS.turned ? TURN_SHUFFLE : STEP_SPEED;
      this.#carry = this.#pose(x, z, facing, speed);
      const nextStep = TURN_MARKS.steps[this.#steps];
      if (nextStep !== undefined && t >= nextStep) {
        this.#events.push({ kind: 'step', index: this.#steps, x, y: hillGroundY(x, z) + 0.1, z });
        this.#steps += 1;
      }
    }
  }

  /** What happened since the last drain, in order. The room voices these on its render step. */
  drainEvents(): EndingEvent[] {
    return this.#events.splice(0, this.#events.length);
  }

  snapshot(): EndingSnapshot {
    const truckX = hillTruckX(this.#truckTime);
    return {
      stage: this.#stage,
      stageTime: this.#stageTime,
      truckX,
      truckGone: hillTruckGone(truckX),
      bellHeld: this.#bellHeld,
      headDip: this.#headDip(),
      carry: this.#carry,
      steps: this.#steps,
    };
  }

  #headDip(): number {
    if (this.#stage === 'the-mouth') {
      const t = this.#stageTime;
      if (t < MOUTH_MARKS.dipped) return smooth(t / MOUTH_MARKS.dipped);
      if (t < MOUTH_MARKS.taken) return 1;
      // Up again, to the carry's hold: the bell's weight keeps her head low.
      return 1 - 0.55 * smooth((t - MOUTH_MARKS.taken) / (MOUTH_MARKS.lifted - MOUTH_MARKS.taken));
    }
    return this.#bellHeld ? 0.45 : 0;
  }

  /** On the line from `x, z` to the bell, TAKE_FROM short of it, facing it. */
  #takeFrom(x: number, z: number): { x: number; z: number; facing: number } {
    const dx = x - HILL_BELL_AT.x;
    const dz = z - HILL_BELL_AT.z;
    const distance = Math.hypot(dx, dz);
    // Standing on it, or nearly: take it from uphill, which is +Z.
    const [ux, uz] = distance < 0.05 ? [0, 1] : [dx / distance, dz / distance];
    const at = { x: HILL_BELL_AT.x + ux * TAKE_FROM, z: HILL_BELL_AT.z + uz * TAKE_FROM };
    return { ...at, facing: Math.atan2(-ux, -uz) };
  }

  #pose(x: number, z: number, facing: number, speed: number): CarriedPose {
    return { x, y: hillGroundY(x, z), z, facing, speed };
  }
}
